import React from 'react';
import { Box, Typography, ButtonBase, Chip } from '@mui/material';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import DescriptionIcon from '@mui/icons-material/Description';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import StarIcon from '@mui/icons-material/Star';
import ConfirmationNumberIcon from '@mui/icons-material/ConfirmationNumber';
import CardGiftcardIcon from '@mui/icons-material/CardGiftcard';
import AccessTimeIcon from '@mui/icons-material/AccessTime';

import { TOKENS, shiftDurationLabel, formatHoursWorked } from '../theme';
import { deriveAssignedDay } from './EmployeeSessionsScreen';

// Spec §2 — five report cards per session.
const REPORTS = [
  {
    id: 'shift',
    label: 'Shift Report',
    Icon: DescriptionIcon,
    bg: TOKENS.blue50,
    color: TOKENS.blue700,
    border: TOKENS.blue200,
  },
  {
    id: 'bonus',
    label: 'Bonus Report',
    Icon: CardGiftcardIcon,
    bg: TOKENS.amber50,
    color: TOKENS.amber700,
    border: TOKENS.amber200,
  },
  {
    id: 'points',
    label: 'Points Report',
    Icon: StarIcon,
    bg: TOKENS.emerald50,
    color: TOKENS.emerald700,
    border: TOKENS.emerald200,
  },
  {
    id: 'ticket',
    label: 'Ticket Out Report',
    Icon: ConfirmationNumberIcon,
    bg: TOKENS.violet50,
    color: TOKENS.violet700,
    border: TOKENS.violet200,
  },
  {
    id: 'raffle',
    label: 'Raffle Report',
    Icon: EmojiEventsIcon,
    bg: TOKENS.pink50,
    color: TOKENS.pink700,
    border: TOKENS.pink200,
  },
];

export default function SessionReportsScreen({
  session,
  employee,
  sessionNumber,
  onSelectReport,
}) {
  const day = deriveAssignedDay(session);
  // Prefer the exact hours computed server-side from timeIn/timeOut; fall
  // back to parsing the clock strings only if it's unavailable.
  const durationLabel =
    formatHoursWorked(session?.hoursWorked) || shiftDurationLabel(session);
  return (
    <Box sx={{ pb: 2 }}>
      <Box sx={{ px: 2, pt: 1.5 }}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            mb: 0.5,
            flexWrap: 'wrap',
          }}
        >
          <Typography
            sx={{ fontSize: 24, fontWeight: 700, color: TOKENS.slate900 }}
          >
            Session #{sessionNumber || '—'}
          </Typography>
          <Chip
            size="small"
            label={day}
            sx={{
              height: 22,
              fontSize: 12,
              fontWeight: 700,
              textTransform: 'uppercase',
              background: TOKENS.blue50,
              color: TOKENS.blue700,
              border: `1px solid ${TOKENS.blue200}`,
            }}
          />
        </Box>
        <Typography sx={{ fontSize: 13, color: TOKENS.slate500 }}>
          {session?.name} · {session?.clockIn || session?.timeIn}
        </Typography>
        <Typography sx={{ fontSize: 13, color: TOKENS.slate400 }}>
          → {session?.clockOut || session?.timeOut || 'In progress'}
        </Typography>
        {/* An open shift has no hours yet — "→ In progress" above says so. */}
        {durationLabel && (
          <Box
            sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5 }}
          >
            <AccessTimeIcon sx={{ fontSize: 15, color: TOKENS.emerald700 }} />
            <Typography
              sx={{ fontSize: 13, fontWeight: 700, color: TOKENS.emerald700 }}
            >
              {`${durationLabel} worked`}
            </Typography>
          </Box>
        )}
      </Box>
      <Typography
        sx={{
          px: 2,
          pt: 2,
          pb: 1,
          fontSize: 13,
          fontWeight: 700,
          textTransform: 'uppercase',
          letterSpacing: '0.05em',
          color: TOKENS.slate500,
        }}
      >
        Reports for this session
      </Typography>
      <Box sx={{ px: 2 }}>
        <Box
          sx={{
            background: '#fff',
            border: `1px solid ${TOKENS.slate200}`,
            borderRadius: 2,
            overflow: 'hidden',
          }}
        >
          {REPORTS.map((r, i) => (
            <ButtonBase
              key={r.id}
              onClick={() => onSelectReport(r.id)}
              sx={{
                width: '100%',
                display: 'flex',
                alignItems: 'center',
                px: 2,
                py: 2,
                textAlign: 'left',
                borderBottom:
                  i < REPORTS.length - 1
                    ? `1px solid ${TOKENS.slate100}`
                    : 'none',
                justifyContent: 'flex-start',
              }}
            >
              <Box
                sx={{
                  width: 48,
                  height: 48,
                  borderRadius: 1.5,
                  background: r.bg,
                  border: `1px solid ${r.border}`,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  mr: 1.5,
                }}
              >
                <r.Icon sx={{ color: r.color, fontSize: 26 }} />
              </Box>
              <Typography
                sx={{
                  flex: 1,
                  fontSize: 17,
                  fontWeight: 700,
                  color: TOKENS.slate900,
                }}
              >
                {r.label}
              </Typography>
              <ChevronRightIcon sx={{ color: TOKENS.slate400 }} />
            </ButtonBase>
          ))}
        </Box>
      </Box>
    </Box>
  );
}
